import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MetadataDefinitionApi } from '../../services/ApiService';

function MetadataDefinitionEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [description, setDescription] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMetadataDefinition = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all document metadata definitions and filter by ID
      const response = await MetadataDefinitionApi.getDocumentMetadataDefinitions();
      if (response.ok) {
        const allDefinitions = await response.json();
        const definition = allDefinitions.find(def => def.id === parseInt(id));
        if (definition) {
          populateForm(definition);
        } else {
          throw new Error(`Metadata definition with ID ${id} not found`);
        }
      } else {
        throw new Error(`Failed to load metadata definitions: ${response.status}`);
      }
    } catch (err) {
      console.error("Error loading metadata definition:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMetadataDefinition();
  }, [loadMetadataDefinition]);

  const populateForm = (definition) => {
    // Map API response fields to form fields
    setName(definition.name || "");
    setFieldType(definition.fieldType || "text"); // API uses 'fieldType'
    setDescription(definition.description || "");
    setDefaultValue(definition.defaultValue || ""); // API uses 'defaultValue'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert("Name is required");
      return;
    }
    if (!fieldType.trim()) {
      alert("Field Type is required");
      return;
    }

    const payload = {
      name: name.trim(),
      fieldType: fieldType, // API expects 'fieldType'
      description: description.trim(),
      defaultValue: defaultValue.trim(), // API expects 'defaultValue'
    };

    try {
      setLoading(true);
      setError(null);

      if (id) {
        // Update existing
        const response = await MetadataDefinitionApi.updateDocumentMetadataDefinition(id, payload);
        if (response.ok) {
          navigate("/settings/document-metadata-definitions");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to update: ${response.status} - ${errorText}`);
        }
      } else {
        // Create new
        const response = await MetadataDefinitionApi.createDocumentMetadataDefinition(payload);
        if (response.ok) {
          navigate("/settings/document-metadata-definitions");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to create: ${response.status} - ${errorText}`);
        }
      }
    } catch (err) {
      console.error("Error saving metadata definition:", err);
      setError(err.message);
      alert(`Error saving metadata definition: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return <div>Loading metadata definition...</div>;
  }

  return (
    <div>
      <h2>{id ? "Edit Document Metadata Definition" : "Create Document Metadata Definition"}</h2>
      
      {error && (
        <div style={{ color: 'var(--color-brand-danger, red)', marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'var(--intent-danger-bg, #ffe6e6)', border: '1px solid var(--intent-danger-border, #ff9999)', borderRadius: '4px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="name">Name:</label>
          <br />
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={loading}
            style={{ width: '300px', padding: '4px' }}
            placeholder="Enter metadata field name"
          />
        </div>

        <div style={{ marginTop: "1rem" }}>
          <label htmlFor="fieldType">Field Type:</label>
          <br />
          <select
            id="fieldType"
            value={fieldType}
            onChange={(e) => setFieldType(e.target.value)}
            disabled={loading}
            style={{ width: '308px', padding: '4px' }}
          >
            <option value="text">Text</option>
            <option value="boolean">Boolean</option>
            <option value="label_list">Label List</option>
            <option value="date">Date</option>
            <option value="number">Number</option>
            <option value="url">URL</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </select>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <label htmlFor="description">Description:</label>
          <br />
          <textarea
            id="description"
            rows="3"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            style={{ width: '300px', padding: '4px' }}
            placeholder="Describe what this metadata field represents"
          />
        </div>

        <div style={{ marginTop: "1rem" }}>
          <label htmlFor="defaultValue">Default Value:</label>
          <br />
          <input
            id="defaultValue"
            type="text"
            value={defaultValue}
            onChange={(e) => setDefaultValue(e.target.value)}
            disabled={loading}
            style={{ width: '300px', padding: '4px' }}
            placeholder="Default value for this field"
          />
        </div>

        <div style={{ marginTop: "1rem" }}>
          <button type="submit" disabled={loading}>
            {loading ? (id ? "Updating..." : "Creating...") : (id ? "Update" : "Create")}
          </button>{" "}
          <button
            type="button"
            onClick={() => navigate("/settings/document-metadata-definitions")}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>

      {id && !loading && !error && (
        <div style={{ marginTop: "2rem", padding: "1rem", backgroundColor: "var(--semantic-surface-panel, #f8f9fa)", border: "1px solid var(--border-subtle, #dee2e6)", borderRadius: "4px" }}>
          <h4>Metadata Definition Info</h4>
          <p><strong>ID:</strong> {id}</p>
          <p><strong>Scope:</strong> DOCUMENT</p>
          <p><strong>Current Field Type:</strong> {fieldType}</p>
        </div>
      )}
    </div>
  );
}

export default MetadataDefinitionEdit;
